import { fileNameFormat } from "../../../common/services/Utilities";
import { styled, Theme } from "@mui/material/styles";
import { KeyValue, Query, SearchPillData } from "../../../interfaces";
import * as React from "react";
import SearchPills from "../../../containers/SearchPills";
import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import {
  sodStatesArray,
  stateNameToAbbrv,
} from "../../../refdata/FilterOptions";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import { useNavigate, useLocation } from "react-router-dom";
import ShowHideButton from "../../ShowHideButton";

const PREFIX = "SummaryTablesTimeSeries";

const classes = {
  tableCellDarken: `${PREFIX}-tableCellDarken`,
  tableHead: `${PREFIX}-tableHead`,
  subHeader: `${PREFIX}-subHeader`,
  tableHeadFirst: `${PREFIX}-tableHeadFirst`,
  subTitle: `${PREFIX}-subTitle`,
  tableBody: `${PREFIX}-tableBody`,
  tableBodyAlt: `${PREFIX}-tableBodyAlt`,
  tableBodyFirstColumn: `${PREFIX}-tableBodyFirstColumn`,
  tableBodyEven: `${PREFIX}-tableBodyEven`,
  tableBodyOdd: `${PREFIX}-tableBodyOdd`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.tableCellDarken}`]: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },

  [`& .${classes.tableHead}`]: {
    border: 0,
    backgroundColor: "#4c77b7",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    textAlign: "center",
  },

  [`& .${classes.subHeader}`]: {
    backgroundColor: "#194480",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    textAlign: "center",
  },

  [`& .${classes.tableHeadFirst}`]: {
    border: 0,
    backgroundColor: "#194480",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
  },

  [`& .${classes.subTitle}`]: {
    backgroundColor: "#194480",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    whiteSpace: "nowrap",
  },

  [`& .${classes.tableBody}`]: {
    "color": "#3485FF",
    "fontFamily": "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    "fontWeight": 700,
    "fontStyle": "normal",
    "lineHeight": "normal",
    "textDecoration": "underline",
    "textAlign": "center",
    "&:hover": { backgroundColor: theme.palette.secondary.main },
    "cursor": "pointer",
  },

  [`& .${classes.tableBodyAlt}`]: {
    color: theme.palette.primary.main,
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    textAlign: "center",
  },

  [`& .${classes.tableBodyFirstColumn}`]: {
    color: "#194480",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontStyle: "normal",
    fontWeight: "bold",
    lineHeight: "normal",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },

  [`& .${classes.tableBodyEven}`]: {
    backgroundColor: "#e7ecf2",
  },

  [`& .${classes.tableBodyOdd}`]: {
    backgroundColor: "#fffff",
  },
}));

interface Props {
  appState: Query;
  updateFilter: (filterField: string, value: any) => void;
  updateUi: (filter: string, value: any) => void;
}

export default function SummaryTablesTimeSeries({
  appState,
  updateFilter,
  updateUi,
}: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Start Date",
      value: `06/30/${appState.filters.startDate}`,
      search: false,
      static: true,
      formKey: "startDate",
    },
    {
      label: "End Date",
      value: `06/30/${appState.filters.endDate}`,
      search: false,
      static: true,
      formKey: "endDate",
    },
    {
      label: "",
      value: appState.ui.institutionTypeTimeSeries,
      search: false,
      static: true,
      formKey: "institutionTypeTimeSeries",
    },
    {
      label: "Totals by",
      value: appState.ui.totalsType,
      search: false,
      static: true,
      formKey: "totalsType",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );
  const [previousStartDate, setPreviousStartDate] = React.useState(
    appState.filters.startDate,
  );
  const [previousEndDate, setPreviousEndDate] = React.useState(
    appState.filters.endDate,
  );
  const [
    previousInstitutionTypeTimeSeries,
    setPreviousInstitutionTypeTimeSeries,
  ] = React.useState(appState.ui.institutionTypeTimeSeries);
  const [previousTotalsType, setPreviousTotalsType] = React.useState(
    appState.ui.totalsType,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [years, setYears] = React.useState(["State"]);
  const [arrays, setArrays] = React.useState<KeyValue>([]);

  const classLookup: KeyValue = React.useMemo(() => {
    return {
      "All Institutions": "all",
      "Commercial Banks": "CB",
      "Savings Institutions": "SA",
      "U.S. Branches of Foreign Banks": "IB",
    };
  }, []);

  React.useEffect(() => {
    if (
      (appState.ui.totalsType !== previousTotalsType ||
        appState.ui.institutionTypeTimeSeries !==
          previousInstitutionTypeTimeSeries ||
        appState.filters.startDate !== previousStartDate ||
        appState.filters.endDate !== previousEndDate ||
        initialLoad) &&
      appState.ui.reportType
    ) {
      setYears(getYears(appState.filters.startDate, appState.filters.endDate));
      initialLoad && setInitialLoad(false);
      setPreviousTotalsType(appState.ui.totalsType);
      setPreviousInstitutionTypeTimeSeries(
        appState.ui.institutionTypeTimeSeries,
      );
      setPreviousStartDate(appState.filters.startDate);
      setPreviousEndDate(appState.filters.endDate);
      let isMounted = true;
      const getUnique =
        appState.ui.totalsType === "Institutions" ? "&get_unique=CERT" : "";
      (async () => {
        await Promise.all(
          getYears(appState.filters.startDate, appState.filters.endDate).map(
            (year) => {
              return fetchData(
                `${ApiBaseUrl()}/sod?agg_by=STALPBR${getUnique}&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${year}${
                  appState.ui.institutionTypeTimeSeries !== "All Institutions"
                    ? ` AND INSURED:${
                        classLookup[appState.ui.institutionTypeTimeSeries]
                      }`
                    : ""
                }`,
              );
            },
          ),
        )
          .then((response) => {
            const arrays: KeyValue[] = [];
            response.forEach((item) => {
              const totalsLookup: KeyValue = {
                Institutions: item.totals?.sum_BKMO,
                Deposits: item.totals?.sum_DEPSUMBR,
              };
              arrays.push([
                ...item.data,
                {
                  totals: totalsLookup[appState.ui.totalsType]
                    ? totalsLookup[appState.ui.totalsType]
                    : item.totals?.count,
                },
              ]);
            });
            if (isMounted) {
              setArrays(arrays);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    appState.filters,
    initialLoad,
    appState.ui,
    years,
    previousStartDate,
    previousEndDate,
    previousTotalsType,
    previousInstitutionTypeTimeSeries,
    classLookup,
  ]);

  const getYears = (startYear: number, endYear: number): string[] => {
    const years: string[] = [];

    for (let year = startYear; year <= endYear; year++) {
      years.push(year.toString());
    }
    return years;
  };

  const tableHeader = ["State", ...years];

  const tableData: KeyValue[] = [];

  tableData.push(["State", ...years]);

  [...sodStatesArray, "Totals**"].forEach((state) => {
    const row = [
      `${state}${state === "Northern Mariana Islands" ? "***" : ""}`,
    ];
    years.forEach((year) => {
      const matchingState =
        arrays[years.indexOf(year)] &&
        arrays[years.indexOf(year)].find(
          (item: KeyValue) => item.STALPBR === stateNameToAbbrv[state],
        );
      const depSumFraction = (value: number) =>
        appState.ui.totalsType === "Deposits"
          ? (value / 1000).toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })
          : value.toLocaleString();
      let rowValue = "0";
      if (matchingState && appState.ui.totalsType === "Institutions") {
        rowValue = matchingState.unique_count;
      }
      if (matchingState && appState.ui.totalsType === "Branch Offices") {
        rowValue = matchingState.count;
      }
      if (matchingState && appState.ui.totalsType === "Deposits") {
        rowValue = (matchingState.sum_DEPSUMBR / 1000)?.toLocaleString(
          undefined,
          {
            maximumFractionDigits: 0,
          },
        );
      }
      if (state === "Totals**") {
        rowValue = depSumFraction(
          arrays[years.indexOf(year)]?.find((sum: KeyValue) => sum.totals)
            ?.totals || 0,
        );
      }
      row.push(rowValue);
    });

    tableData.push(row);
  });

  type Props = {
    tableData: KeyValue[];
  };

  const [downloadHeaderRow, ...downloadBodyRows] = tableData as string[][];

  const downloadObjects: KeyValue[] = downloadBodyRows.map((row) =>
    downloadHeaderRow.reduce((obj: KeyValue, key: string, i: number) => {
      obj[key] = row[i];
      return obj;
    }, {}),
  );
  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        `Summary_Tables_Time_Series_${appState.ui.institutionTypeTimeSeries}_${appState.ui.totalsType}`,
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      footnotes: [
        appState.filters.startDate === "1994"
          ? "*Includes 18 conservatorships"
          : null,
        " **Institution amounts represent the number of unique institutions that maintain a deposit taking office in each state as of June 30.",
      ].filter(Boolean) as string[],
    },
  ];

  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        Time Series by States & Territories
      </Typography>
      <ApiDataDownload
        app="SOD"
        apiData={apiData}
        downloadData={downloadObjects}
      />
    </Box>
  );

  const tableCellProps = (
    instType: string,
    year: string,
    rowData: any,
    noOnClick: boolean,
  ) => {
    const props = {
      onClick: noOnClick
        ? undefined
        : (e: any) => {
            updateFilter("stalpbr", stateNameToAbbrv[rowData[0]]);
            updateUi("reportType", "nationalTimeSeries");
            updateFilter("lastYear", year);
            updateUi("summaryTableLevelTwo", true);
            updateUi("summaryTableType", "nationalTimeSeries");
            if (e.key === "Enter" || e.nativeEvent.type === "click") {
              navigate(
                {
                  pathname: "/SOD/summaryTables",
                  search: `?summaryTableLevelTwo=true&instType=${instType}&reportType=nationalTimeSeries`,
                },
                {
                  state: {
                    prevAppState: appState,
                    prevPath: `${location.pathname}${location.search}`,
                  },
                },
              );
            }
          },
      tabIndex: 0,
    };

    return props;
  };
  const TimeSeriesTable: React.FC<Props> = ({ tableData }) => {
    const tableDataBody = [...tableData];
    tableDataBody.pop();
    const instType = classLookup[appState.ui.institutionTypeTimeSeries];
    return (
      <Box
        sx={{
          overflowX: "auto",
        }}
      >
        <TableContainer sx={{ maxHeight: 1500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {tableHeader.map((header: string, index: number) => (
                  <TableCell
                    key={index}
                    className={
                      index === 0 ? classes.tableHeadFirst : classes.tableHead
                    }
                  >
                    {header === "1994" ? "1994*" : header}
                  </TableCell>
                ))}
              </TableRow>
              {appState.ui.totalsType === "Deposits" && (
                <TableRow>
                  {tableHeader.map((header: string, index: number) => (
                    <TableCell className={classes.subTitle} key={index}>
                      {index === 0 ? "Deposit Amounts In Millions USD" : ""}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {tableDataBody.slice(1).map((row, index) => (
                <TableRow
                  className={
                    index % 2 === 1
                      ? classes.tableBodyEven
                      : classes.tableBodyOdd
                  }
                  key={index}
                >
                  {row.map((cell: string, index: number) => (
                    <TableCell
                      {...tableCellProps(
                        instType,
                        years[index - 1],
                        row,
                        cell === "0" || index === 0,
                      )}
                      className={
                        index === 0
                          ? classes.tableBodyFirstColumn
                          : cell === "0" || index === 0
                            ? classes.tableBodyAlt
                            : classes.tableBody
                      }
                      key={index}
                    >
                      {cell ? cell.toLocaleString() : 0}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                {tableData[tableData.length - 1].map(
                  (element: string, index: number) => (
                    <TableCell
                      className={
                        index === 0 ? classes.tableHeadFirst : classes.subHeader
                      }
                      key={index}
                    >
                      {element}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };
  const footerSx = {
    color: (theme: Theme) => theme.palette.primary.main,
    width: 900,
    minWidth: 900,
    fontSize: 16,
    fontWeight: "bold",
  };
  return (
    <Root>
      {headingTitle}
      <Divider />
      {searchPills}
      <TimeSeriesTable tableData={tableData} />
      {appState.filters.startDate === "1994" && (
        <Typography sx={footerSx}>*Includes 18 conservatorships</Typography>
      )}
      <Typography sx={footerSx}>
        **Institution amounts represent the number of unique institutions that
        maintain a deposit taking office in each state as of June 30.
      </Typography>
      <Typography sx={footerSx}>
        ***AKA Commonwealth of the Northern Mariana Islands
      </Typography>
    </Root>
  );
}
